/* eslint-disable no-magic-numbers */
/**
 * Delta E (color difference) calculation utilities
 * Extracted from delta-e library to reduce dependencies
 * Implements CIEDE2000 color difference algorithm
 */

export type Lab = { L: number; A: number; B: number };

/**
 * Calculate CIEDE2000 color difference between two LAB colors
 * This is the most accurate perceptual color difference formula
 * @param lab1 - First LAB color
 * @param lab2 - Second LAB color
 * @returns Delta E value (lower = more similar)
 */
export function getDeltaE00(lab1: Lab, lab2: Lab): number {
  const L1 = lab1.L;
  const a1 = lab1.A;
  const b1 = lab1.B;

  const L2 = lab2.L;
  const a2 = lab2.A;
  const b2 = lab2.B;

  // Calculate C* (chroma) for both colors
  const C1 = Math.sqrt(a1 * a1 + b1 * b1);
  const C2 = Math.sqrt(a2 * a2 + b2 * b2);

  // Calculate mean chroma
  const Cbar = (C1 + C2) / 2;

  // Calculate G (chroma weighting factor)
  const G =
    0.5 *
    (1 - Math.sqrt(Math.pow(Cbar, 7) / (Math.pow(Cbar, 7) + Math.pow(25, 7))));

  // Adjust a* values
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;

  // Recalculate C* and h* with adjusted a*
  const C1p = Math.sqrt(a1p * a1p + b1 * b1);
  const C2p = Math.sqrt(a2p * a2p + b2 * b2);

  // Calculate h* (hue angle)
  let h1p = Math.atan2(b1, a1p) * (180 / Math.PI);
  let h2p = Math.atan2(b2, a2p) * (180 / Math.PI);

  // Normalize h* to 0-360
  if (h1p < 0) h1p += 360;
  if (h2p < 0) h2p += 360;

  // Calculate delta L', delta C', delta H'
  const deltaLp = L2 - L1;
  const deltaCp = C2p - C1p;

  let deltaHp = h2p - h1p;
  if (Math.abs(deltaHp) > 180) {
    if (deltaHp > 0) {
      deltaHp -= 360;
    } else {
      deltaHp += 360;
    }
  }
  deltaHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((deltaHp * Math.PI) / 360);

  // Calculate mean L'
  const Lpbar = (L1 + L2) / 2;

  // Calculate mean C'
  const Cpbar = (C1p + C2p) / 2;

  // Calculate mean h'
  let hpbar = (h1p + h2p) / 2;
  if (Math.abs(h1p - h2p) > 180) {
    if (h1p + h2p < 360) {
      hpbar += 180;
    } else {
      hpbar -= 180;
    }
  }

  // Calculate T (hue rotation term)
  const T =
    1 -
    0.17 * Math.cos((hpbar - 30) * (Math.PI / 180)) +
    0.24 * Math.cos(2 * hpbar * (Math.PI / 180)) +
    0.32 * Math.cos((3 * hpbar + 6) * (Math.PI / 180)) -
    0.2 * Math.cos((4 * hpbar - 63) * (Math.PI / 180));

  // Calculate delta theta
  const deltaTheta = 30 * Math.exp(-Math.pow((hpbar - 275) / 25, 2));

  // Calculate R_C (chroma rotation term)
  const R_C =
    2 * Math.sqrt(Math.pow(Cpbar, 7) / (Math.pow(Cpbar, 7) + Math.pow(25, 7)));

  // Calculate R_T (hue rotation term)
  const R_T = -Math.sin(2 * deltaTheta * (Math.PI / 180)) * R_C;

  // Calculate weighting functions
  const kL = 1;
  const kC = 1;
  const kH = 1;

  const SL =
    1 +
    (0.015 * Math.pow(Lpbar - 50, 2)) / Math.sqrt(20 + Math.pow(Lpbar - 50, 2));

  const SC = 1 + 0.045 * Cpbar;

  const SH = 1 + 0.015 * Cpbar * T;

  // Calculate final delta E
  const deltaE = Math.sqrt(
    Math.pow(deltaLp / (kL * SL), 2) +
      Math.pow(deltaCp / (kC * SC), 2) +
      Math.pow(deltaHp / (kH * SH), 2) +
      R_T * (deltaCp / (kC * SC)) * (deltaHp / (kH * SH))
  );

  return deltaE;
}
